import {
  SubsectionTitle,
  SubsectionWrapper,
} from '@components/LayoutTabs/SubsectionReusableComponents'
import useRecentMatchOutcomes from '@hooks/useRecentMatchOutcomes'
import { FunctionComponent, useMemo } from 'react'
import styled, { css } from 'styled-components'
import translate from '@i18n'
import { SvgIconName } from '@components/SvgIcon/types'
import Placeholder from '@components/LayoutTabs/SubsectionReusableComponents/Placeholder'
import { desktopCSS } from '@measures/responsive'
import OutcomeData from './OutcomeData'

export interface RecentMatchOutcomesProps {
  title: string
  matchId: string
  typeOfSport: 'soccer'
}

const StyledContentWrapper = styled.div`
  ${({
    theme: {
      spacing: { spacing2, spacing8, spacing64 },
    },
  }) => css`
    display: flex;
    gap: ${spacing8};
    min-height: calc(${spacing64} + ${spacing2});
  `}
`

const StyledMatchOutcomesWrapper = styled.div`
  ${({
    theme: {
      spacing: { spacing4, spacing8 },
      colors: { strokeWeak },
    },
  }) => css`
    display: flex;
    flex-direction: column;
    flex-grow: 1;
    align-items: center;
    width: 100%;
    border-radius: ${spacing4};
    border: 1px solid ${strokeWeak};
    padding-bottom: ${spacing8};
    box-sizing: border-box;
    height: 100%;
  `}
`
const StyledTeamWrapper = styled.div`
  ${({
    theme: {
      spacing: { spacing12, spacing20 },
    },
  }) => css`
    box-sizing: border-box;
    width: 100%;
    display: flex;
    justify-content: center;
    gap: ${spacing12};
    flex-direction: row-reverse;
    flex-wrap: wrap;
    height: 44px;
    overflow: hidden;

    ${desktopCSS(css`
      gap: ${spacing20};
    `)}
  `}
`

const StyledTeamAbbreviation = styled.div`
  ${({
    theme: {
      spacing: { spacing8, spacing12 },
      colors: { fillDefault },
      typography: {
        catchword: {
          medium2: { bundledCSS: medium2CatchwordCSS },
        },
      },
    },
  }) => css`
    position: relative;
    top: -${spacing8};
    background-color: ${fillDefault};
    padding: 0 ${spacing8};
    ${medium2CatchwordCSS};
    line-height: ${spacing12};
  `}
`

const RecentMatchOutcomes: FunctionComponent<RecentMatchOutcomesProps> = ({
  title,
  matchId,
  typeOfSport,
}) => {
  const {
    data: recentMatchOutcomesData,
    error,
    isFetched,
  } = useRecentMatchOutcomes({
    matchId,
    typeOfSport,
  })

  const hasNonEmptyData =
    recentMatchOutcomesData &&
    (recentMatchOutcomesData.homeTeam.recentMatchOutcomes.length > 0 ||
      recentMatchOutcomesData.awayTeam.recentMatchOutcomes.length > 0)

  const recentOutcomesContent = useMemo(() => {
    if (hasNonEmptyData) {
      return (
        <>
          {[
            recentMatchOutcomesData.homeTeam,
            recentMatchOutcomesData.awayTeam,
          ].map((team) => (
            <StyledMatchOutcomesWrapper key={team.abbreviation}>
              <StyledTeamAbbreviation>
                {team.abbreviation}
              </StyledTeamAbbreviation>
              <StyledTeamWrapper>
                <OutcomeData team={team} />
              </StyledTeamWrapper>
            </StyledMatchOutcomesWrapper>
          ))}
        </>
      )
    } else {
      const placeholderMessage = error
        ? {
            iconName: 'user-robot-xmarks-solid' as SvgIconName,
            title: translate('network.error.title'),
            text: translate('network.error.text'),
          }
        : isFetched
          ? {
              iconName: 'calendar-clock' as SvgIconName,
              title: translate('sports.emptyData.title'),
              text: translate('sports.emptyData.text'),
            }
          : undefined

      return placeholderMessage ? (
        <Placeholder
          iconName={placeholderMessage.iconName}
          title={placeholderMessage.title}
          text={placeholderMessage.text}
        />
      ) : null
    }
  }, [error, hasNonEmptyData, isFetched, recentMatchOutcomesData])

  return (
    <SubsectionWrapper>
      <SubsectionTitle>{title}</SubsectionTitle>
      <StyledContentWrapper>{recentOutcomesContent}</StyledContentWrapper>
    </SubsectionWrapper>
  )
}

export default RecentMatchOutcomes
